import { LegalPage } from "../../components/LegalPage";

export function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      description="WeConsult Rx privacy policy: what we collect, how we use it, our third parties, retention, security, and your rights under GDPR and CCPA."
      path="/privacy"
      effective="2026-08-04"
    >
      <p>
        WeConsult Rx (&ldquo;we&rdquo;, &ldquo;us&rdquo;), operated by DRX
        Consulting Group, LLC, provides a consultant-pharmacy compliance
        platform at app.weconsultrx.com (the &ldquo;Platform&rdquo;) for
        skilled nursing, assisted-living, and dialysis facilities. This
        Privacy Policy explains how we collect, use, disclose, and safeguard
        information in connection with weconsultrx.com and the Platform. By
        accessing the Platform, you consent to the practices described here.
      </p>

      <h2>1. Information we collect</h2>
      <p>
        <strong>1.1 Information you provide:</strong> name, work email,
        phone, organization, role, demo/contact form submissions, newsletter
        subscriptions, and (if you opt in) your mobile phone number for SMS
        notifications.
      </p>
      <p>
        <strong>1.2 Automatically collected:</strong> login events, audit log
        entries, feature usage, IP address, browser user agent, request
        timestamps, and aggregated performance/error telemetry (no PHI).
      </p>
      <p>
        <strong>1.3 Customer content:</strong> customer content, including
        PHI (resident charts, medication regimens, review outcomes), is
        processed only under a Business Associate Agreement (BAA). It is
        owned and controlled by the customer, not by WeConsult Rx. See our{" "}
        <a href="/baa">BAA</a>.
      </p>

      <h2>2. How we use information</h2>
      <ul>
        <li>To provide, operate, and improve the Platform.</li>
        <li>To respond to demo / contact requests.</li>
        <li>
          To send transactional notifications (review reminders, license
          renewals, bundle status) by email and, if you opt in, SMS.
        </li>
        <li>To send marketing email, only with your consent.</li>
        <li>
          To sync calendar events (title/time only, via Google or Microsoft
          OAuth) when you connect a calendar.
        </li>
        <li>To meet legal, regulatory, and HIPAA obligations.</li>
        <li>To investigate fraud, abuse, or security incidents.</li>
      </ul>
      <p>
        We do <strong>not</strong> sell personal information. We do not run
        third-party advertising trackers. We do not share customer content
        for AI training.
      </p>

      <h2>3. How we share information</h2>
      <p>
        We do not sell, rent, or trade your information to third parties for
        marketing. We share information only with subprocessors bound by
        data processing agreements, needed to operate the Platform. See the
        full list, purposes, and BAA status of every subprocessor on our{" "}
        <a href="/subprocessors">Subprocessors</a> page.
      </p>
      <p>
        <strong>AI data processing:</strong> chart-review synthesis uses AI
        inference on de-identified prompts only. Prompts are stripped to the
        HIPAA Safe Harbor standard before they leave our environment, and
        de-identification is verified at the egress boundary — a prompt
        containing residual identifiers is blocked rather than sent. Our
        primary inference provider operates under a signed BAA; a secondary
        provider is used only as failover and never receives PHI.
      </p>

      <h2>4. Text messaging (SMS)</h2>
      <p>
        If you opt in, from Account Settings or the SMS opt-in checkbox on
        our public forms, we collect your mobile phone number and a
        timestamped consent record (the disclosure text and version you
        agreed to). We use that phone number solely to send you SMS
        reminders (e.g. items due for review) via Twilio. Message content is
        limited to facility-level, non-clinical information — no patient,
        resident, or diagnosis data is ever included in a text message.
      </p>
      <p>
        Message frequency varies with your facility&rsquo;s activity; a
        typical user receives between 0 and 8 messages per week. Message and
        data rates may apply. Reply STOP to opt out or HELP for help. Full
        program details are in our <a href="/sms-terms">SMS Terms</a>.
      </p>
      <div className="rounded-2xl border-l-4 border-forest-700 bg-forest-50 p-5 my-4">
        <p className="font-semibold text-forest-900">
          Mobile information and messaging consent are not shared with third
          parties or affiliates for marketing or promotional purposes.
        </p>
      </div>
      <p>
        The phone number and consent record collected during SMS opt-in are
        used only to deliver the notifications you signed up for. We share
        the phone number with Twilio (our SMS delivery provider) solely to
        send messages, and with our database provider (Supabase) to store
        the consent record — both bound by data processing agreements. We do
        not sell, rent, lease, or otherwise transfer your phone number or SMS
        opt-in status to any other party for marketing, advertising, or
        promotional purposes.
      </p>

      <h2>5. Data retention</h2>
      <ul>
        <li>Account data: for the duration of the account + 90 days.</li>
        <li>
          AI prompts: not retained — inference is stateless and prompts are
          discarded after the response is returned.
        </li>
        <li>Audit log: 7 years (HIPAA §164.530(j)).</li>
        <li>Customer content: per the Master Services Agreement.</li>
        <li>Marketing leads: 18 months unless extended by ongoing dialogue.</li>
        <li>
          SMS consent records: for as long as you remain opted in, plus a
          reasonable audit-trail period after opt-out.
        </li>
      </ul>
      <p>
        To request deletion of your data, email privacy@weconsultrx.com.
        Requests are processed within 30 days, subject to legal retention
        requirements (e.g. the HIPAA audit-log period above).
      </p>

      <h2>6. Data security</h2>
      <ul>
        <li>TLS 1.2+ encryption in transit; AES-256 encryption at rest.</li>
        <li>
          Row-level security (RLS) enforcing per-tenant data isolation at the
          database layer.
        </li>
        <li>Short-lived JWT authentication tokens.</li>
        <li>Automated audit logging of access to customer content.</li>
        <li>Role-based access controls, least privilege.</li>
        <li>Regular security review and dependency auditing.</li>
      </ul>

      <h2>7. Your rights</h2>
      <p>
        <strong>All users:</strong> you may access, correct, delete, or port
        your personal information, and object to processing for specific
        purposes. Email privacy@weconsultrx.com to exercise any of these
        rights.
      </p>
      <p>
        <strong>California residents (CCPA/CPRA):</strong> we do not sell
        personal information. See <a href="/ccpa">/ccpa</a> for your
        California-specific rights.
      </p>
      <p>
        <strong>EU/UK residents (GDPR):</strong> we process data under
        legitimate interest and consent. See <a href="/gdpr">/gdpr</a> for
        your EU/UK rights, or contact our Data Protection Officer at
        privacy@weconsultrx.com.
      </p>

      <h2>8. Cookies</h2>
      <p>
        See our <a href="/cookies">Cookie Policy</a> for details on cookies
        and local storage used by weconsultrx.com and the Platform.
      </p>

      <h2>9. Children&rsquo;s privacy</h2>
      <p>
        The Platform is intended for use by licensed healthcare
        professionals and facility staff and is not directed to individuals
        under 18. We do not knowingly collect information from children.
      </p>

      <h2>10. International transfers</h2>
      <p>
        Our infrastructure is primarily hosted in the United States. EU/UK
        transfers rely on Standard Contractual Clauses incorporated into our
        Data Processing Agreement.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We will post material changes here and update the effective date.
        Material changes require at least 30 days&rsquo; notice; for active
        customers we will additionally notify by email.
      </p>

      <h2>12. Contact</h2>
      <p>
        Data Protection Officer &middot; privacy@weconsultrx.com
        <br />
        General support &middot; support@weconsultrx.com
      </p>
    </LegalPage>
  );
}
